/**
 * Cmd+K result ranking — PURE (no React, no Firebase), so the golden-set scorer
 * (scripts/search/golden-score.ts) can grade the REAL ranking outside the browser.
 * Moved verbatim from hooks/useGlobalSearch.ts, which re-exports prioritizeResults.
 */
import { MODULE_COLLECTION_PRIORITY, NOISY_COLLECTION_WEIGHT } from "../../config/typesense";
import type { QueryIntent } from "../typesense-service";
import type { ModuleId } from "../../modules/types";
import type { MultiSearchResult } from "../../types/search";

/** Sort collection groups by their top hit's text_match score (descending). */
function sortByTopScore(groups: MultiSearchResult["results"]): MultiSearchResult["results"] {
  return [...groups].sort((a, b) => {
    const scoreA = a.hits[0]?.score ?? 0;
    const scoreB = b.hits[0]?.score ?? 0;
    return scoreB - scoreA;
  });
}

/**
 * Sort collection groups by their top hit's text_match score (descending),
 * dampening collections in NOISY_COLLECTION_WEIGHT so high-row-count
 * collections (e.g. call_logs) don't win purely on volume when they land in
 * the unprioritized "Other results" bucket. Only used for that bucket —
 * explicit high-confidence intent matches (e.g. a phone number query, which
 * legitimately wants call_logs to rank highly) use the undamped sort above.
 */
function sortByTopScoreDamped(groups: MultiSearchResult["results"]): MultiSearchResult["results"] {
  return [...groups].sort((a, b) => {
    const scoreA = (a.hits[0]?.score ?? 0) * (NOISY_COLLECTION_WEIGHT[a.collection] ?? 1);
    const scoreB = (b.hits[0]?.score ?? 0) * (NOISY_COLLECTION_WEIGHT[b.collection] ?? 1);
    return scoreB - scoreA;
  });
}

/**
 * Sort collection groups by their position in an explicit priority list —
 * a collection listed earlier always renders above one listed later. This
 * is what makes MODULE_COLLECTION_PRIORITY's array order authoritative
 * instead of cosmetic. Collections absent from priorityList rank last
 * (rather than indexOf's -1 sorting them first) — defensive in case a
 * caller ever passes an unfiltered group list.
 */
function sortByPriorityOrder(
  groups: MultiSearchResult["results"],
  priorityList: readonly string[],
): MultiSearchResult["results"] {
  const rank = (collection: string) => {
    const index = priorityList.indexOf(collection);
    return index === -1 ? Number.MAX_SAFE_INTEGER : index;
  };
  return [...groups].sort((a, b) => rank(a.collection) - rank(b.collection));
}

/** Partition and reorder collection groups by intent, score, and module. */
export function prioritizeResults(
  results: MultiSearchResult["results"],
  moduleId: ModuleId | null,
  intent: QueryIntent,
): {
  prioritized: MultiSearchResult["results"];
  rest: MultiSearchResult["results"];
  sectionLabel: string;
} {
  // High-confidence intent: boosted collections form the top section
  if (intent.confidence === "high" && intent.boostedCollections.length > 0) {
    const boostedSet = new Set(intent.boostedCollections);
    const boosted: MultiSearchResult["results"] = [];
    const rest: MultiSearchResult["results"] = [];

    for (const group of results) {
      if (boostedSet.has(group.collection)) {
        boosted.push(group);
      } else {
        rest.push(group);
      }
    }

    return {
      // An ordered intent (a pasted Generator ID) fixes the order; otherwise the
      // best-scoring boosted collection leads.
      prioritized: intent.ordered ?
        sortByPriorityOrder(boosted, intent.boostedCollections) :
        sortByTopScore(boosted),
      rest: sortByTopScoreDamped(rest),
      sectionLabel: "Best matches",
    };
  }

  // Low confidence or no intent: use module priority for the "in this module" tier
  const priorityList = moduleId ? MODULE_COLLECTION_PRIORITY[moduleId] : undefined;

  if (!priorityList || priorityList.length === 0) {
    return {
      prioritized: [],
      rest: sortByTopScoreDamped(results),
      sectionLabel: "Results",
    };
  }

  const prioritySet = new Set(priorityList);
  const inModule: MultiSearchResult["results"] = [];
  const rest: MultiSearchResult["results"] = [];

  // Low-confidence intent: give boosted collections tiebreaker advantage in "rest"
  const boostedSet = new Set(intent.boostedCollections);

  for (const group of results) {
    if (prioritySet.has(group.collection)) {
      inModule.push(group);
    } else {
      rest.push(group);
    }
  }

  // "In this module" tier: MODULE_COLLECTION_PRIORITY's array order is
  // authoritative — a collection listed earlier always renders first.
  // "Other results" tier: score-based, dampened for noisy collections, with
  // a 10% tiebreaker bump for low-confidence intent matches.
  const sortRestWithBoost = (groups: MultiSearchResult["results"]) =>
    [...groups].sort((a, b) => {
      const rawA = (a.hits[0]?.score ?? 0) * (NOISY_COLLECTION_WEIGHT[a.collection] ?? 1);
      const rawB = (b.hits[0]?.score ?? 0) * (NOISY_COLLECTION_WEIGHT[b.collection] ?? 1);
      const scoreA = boostedSet.has(a.collection) ? rawA * 1.1 : rawA;
      const scoreB = boostedSet.has(b.collection) ? rawB * 1.1 : rawB;
      return scoreB - scoreA;
    });

  return {
    prioritized: sortByPriorityOrder(inModule, priorityList),
    rest: sortRestWithBoost(rest),
    sectionLabel: "In this module",
  };
}
